import { makeAutoObservable } from "mobx";
import { RootStore } from "@intrasync/stores";
import {
  LegalEntity,
  IntercompanyTransaction,
  ConsolidatedFinancials,
  AuditLog,
  AuditReport,
  DataMigrationLog,
  ComplianceReport,
  ComplianceRule,
  ComplianceViolation,
  DocumentRetentionPolicy,
  RetainedDocument,
  ComplianceCertification,
  EntityType,
  EntityStatus,
  IntercompanyTransactionType,
  IntercompanyTransactionStatus,
  AuditEventType,
  AuditSeverity,
  ComplianceFramework,
  ComplianceStatus,
  DocumentRetentionStatus,
  EntityAddress,
  EntityContact,
  EntityBanking,
  EntitySettings,
} from "../types";

export default class AdvancedFeaturesStore {
  rootStore: RootStore;

  // UI State
  selectedTab: number = 0;
  selectedEntityId: string | null = null;
  selectedAuditLogId: string | null = null;
  selectedComplianceReportId: string | null = null;

  // Dialog States
  entityDialogOpen: boolean = false;
  intercompanyTxDialogOpen: boolean = false;
  consolidationDialogOpen: boolean = false;
  auditReportDialogOpen: boolean = false;
  complianceReportDialogOpen: boolean = false;
  complianceRuleDialogOpen: boolean = false;
  violationDialogOpen: boolean = false;
  retentionPolicyDialogOpen: boolean = false;
  certificationDialogOpen: boolean = false;

  // Form Data
  entityFormData: Partial<LegalEntity> = this.getInitialEntityFormData();
  intercompanyTxFormData: Partial<IntercompanyTransaction> = this.getInitialIntercompanyTxFormData();
  complianceReportFormData: Partial<ComplianceReport> = this.getInitialComplianceReportFormData();
  complianceRuleFormData: Partial<ComplianceRule> = this.getInitialComplianceRuleFormData();
  retentionPolicyFormData: Partial<DocumentRetentionPolicy> = this.getInitialRetentionPolicyFormData();
  certificationFormData: Partial<ComplianceCertification> = this.getInitialCertificationFormData();

  // Edit Mode
  isEditMode: boolean = false;

  // Filters
  entityStatusFilter: EntityStatus | "all" = "all";
  entityTypeFilter: EntityType | "all" = "all";
  auditEventTypeFilter: AuditEventType | "all" = "all";
  auditSeverityFilter: AuditSeverity | "all" = "all";
  complianceFrameworkFilter: ComplianceFramework | "all" = "all";
  complianceStatusFilter: ComplianceStatus | "all" = "all";
  dateRangeFilter: { startDate: string; endDate: string } = {
    startDate: "",
    endDate: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ===================================================================
  // SERVICE STORE GETTERS
  // ===================================================================

  get legalEntitiesStore() {
    return this.rootStore["legalEntitiesStore" as keyof RootStore] as {
      entities: LegalEntity[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
      remove: (id: string) => Promise<any>;
    };
  }

  get intercompanyTransactionsStore() {
    return this.rootStore["intercompanyTransactionsStore" as keyof RootStore] as {
      entities: IntercompanyTransaction[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  get consolidatedFinancialsStore() {
    return this.rootStore["consolidatedFinancialsStore" as keyof RootStore] as {
      entities: ConsolidatedFinancials[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
    };
  }

  get auditLogsStore() {
    return this.rootStore["auditLogsStore" as keyof RootStore] as {
      entities: AuditLog[];
      find: (params?: any) => Promise<any>;
    };
  }

  get auditReportsStore() {
    return this.rootStore["auditReportsStore" as keyof RootStore] as {
      entities: AuditReport[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
    };
  }

  get dataMigrationLogsStore() {
    return this.rootStore["dataMigrationLogsStore" as keyof RootStore] as {
      entities: DataMigrationLog[];
      find: (params?: any) => Promise<any>;
    };
  }

  get complianceReportsStore() {
    return this.rootStore["complianceReportsStore" as keyof RootStore] as {
      entities: ComplianceReport[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  get complianceRulesStore() {
    return this.rootStore["complianceRulesStore" as keyof RootStore] as {
      entities: ComplianceRule[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
      remove: (id: string) => Promise<any>;
    };
  }

  get complianceViolationsStore() {
    return this.rootStore["complianceViolationsStore" as keyof RootStore] as {
      entities: ComplianceViolation[];
      find: (params?: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  get documentRetentionPoliciesStore() {
    return this.rootStore["documentRetentionPoliciesStore" as keyof RootStore] as {
      entities: DocumentRetentionPolicy[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  get retainedDocumentsStore() {
    return this.rootStore["retainedDocumentsStore" as keyof RootStore] as {
      entities: RetainedDocument[];
      find: (params?: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  get complianceCertificationsStore() {
    return this.rootStore["complianceCertificationsStore" as keyof RootStore] as {
      entities: ComplianceCertification[];
      find: (params?: any) => Promise<any>;
      create: (data: any) => Promise<any>;
      patch: (id: string, data: any) => Promise<any>;
    };
  }

  // ===================================================================
  // COMPUTED PROPERTIES - MULTI-ENTITY
  // ===================================================================

  get entities(): LegalEntity[] {
    let entities = this.legalEntitiesStore?.entities || [];

    if (this.entityStatusFilter !== "all") {
      entities = entities.filter((e) => e.status === this.entityStatusFilter);
    }

    if (this.entityTypeFilter !== "all") {
      entities = entities.filter((e) => e.entityType === this.entityTypeFilter);
    }

    return entities.sort((a, b) =>
      a.entityName.localeCompare(b.entityName)
    );
  }

  get intercompanyTransactions(): IntercompanyTransaction[] {
    const transactions = this.intercompanyTransactionsStore?.entities || [];
    return transactions.sort((a, b) =>
      new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
    );
  }

  get consolidatedFinancials(): ConsolidatedFinancials[] {
    const financials = this.consolidatedFinancialsStore?.entities || [];
    return financials.sort((a, b) =>
      b.periodMonth.localeCompare(a.periodMonth)
    );
  }

  get parentEntities(): LegalEntity[] {
    return this.entities.filter((e) => e.entityType === EntityType.PARENT);
  }

  get subsidiaryEntities(): LegalEntity[] {
    return this.entities.filter((e) => e.entityType === EntityType.SUBSIDIARY);
  }

  get entityStats() {
    const entities = this.legalEntitiesStore?.entities || [];
    return {
      total: entities.length,
      active: entities.filter((e) => e.status === EntityStatus.ACTIVE).length,
      inactive: entities.filter((e) => e.status === EntityStatus.INACTIVE).length,
      parent: entities.filter((e) => e.entityType === EntityType.PARENT).length,
      subsidiary: entities.filter((e) => e.entityType === EntityType.SUBSIDIARY).length,
    };
  }

  // ===================================================================
  // COMPUTED PROPERTIES - AUDIT TRAIL
  // ===================================================================

  get auditLogs(): AuditLog[] {
    let logs = this.auditLogsStore?.entities || [];

    if (this.auditEventTypeFilter !== "all") {
      logs = logs.filter((log) => log.eventType === this.auditEventTypeFilter);
    }

    if (this.auditSeverityFilter !== "all") {
      logs = logs.filter((log) => log.severity === this.auditSeverityFilter);
    }

    if (this.dateRangeFilter.startDate) {
      logs = logs.filter(
        (log) => new Date(log.timestamp) >= new Date(this.dateRangeFilter.startDate)
      );
    }

    if (this.dateRangeFilter.endDate) {
      logs = logs.filter(
        (log) => new Date(log.timestamp) <= new Date(this.dateRangeFilter.endDate)
      );
    }

    return logs.sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  get auditReports(): AuditReport[] {
    return (this.auditReportsStore?.entities || []).sort((a, b) =>
      new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
    );
  }

  get dataMigrationLogs(): DataMigrationLog[] {
    return (this.dataMigrationLogsStore?.entities || []).sort((a, b) =>
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );
  }

  get auditStats() {
    const logs = this.auditLogsStore?.entities || [];
    return {
      total: logs.length,
      critical: logs.filter((l) => l.severity === AuditSeverity.CRITICAL).length,
      high: logs.filter((l) => l.severity === AuditSeverity.HIGH).length,
      medium: logs.filter((l) => l.severity === AuditSeverity.MEDIUM).length,
      low: logs.filter((l) => l.severity === AuditSeverity.LOW).length,
      last24Hours: logs.filter(
        (l) => new Date(l.timestamp).getTime() > Date.now() - 24 * 60 * 60 * 1000
      ).length,
    };
  }

  // ===================================================================
  // COMPUTED PROPERTIES - COMPLIANCE
  // ===================================================================

  get complianceReports(): ComplianceReport[] {
    let reports = this.complianceReportsStore?.entities || [];

    if (this.complianceFrameworkFilter !== "all") {
      reports = reports.filter((r) => r.framework === this.complianceFrameworkFilter);
    }

    if (this.complianceStatusFilter !== "all") {
      reports = reports.filter((r) => r.status === this.complianceStatusFilter);
    }

    return reports.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  get complianceRules(): ComplianceRule[] {
    return (this.complianceRulesStore?.entities || []).sort((a, b) =>
      a.ruleName.localeCompare(b.ruleName)
    );
  }

  get complianceViolations(): ComplianceViolation[] {
    return (this.complianceViolationsStore?.entities || []).sort((a, b) =>
      new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
    );
  }

  get retentionPolicies(): DocumentRetentionPolicy[] {
    return (this.documentRetentionPoliciesStore?.entities || []).sort((a, b) =>
      a.policyName.localeCompare(b.policyName)
    );
  }

  get retainedDocuments(): RetainedDocument[] {
    return (this.retainedDocumentsStore?.entities || []).sort((a, b) =>
      new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()
    );
  }

  get certifications(): ComplianceCertification[] {
    return (this.complianceCertificationsStore?.entities || []).sort((a, b) =>
      new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime()
    );
  }

  get complianceStats() {
    const reports = this.complianceReportsStore?.entities || [];
    const violations = this.complianceViolationsStore?.entities || [];
    return {
      totalReports: reports.length,
      compliant: reports.filter((r) => r.status === ComplianceStatus.COMPLIANT).length,
      nonCompliant: reports.filter((r) => r.status === ComplianceStatus.NON_COMPLIANT).length,
      pendingReview: reports.filter((r) => r.status === ComplianceStatus.PENDING_REVIEW).length,
      openViolations: violations.filter((v) => v.status === "open").length,
      criticalViolations: violations.filter((v) => v.severity === "critical").length,
    };
  }

  // ===================================================================
  // ACTIONS - UI STATE
  // ===================================================================

  setSelectedTab = (tab: number) => {
    this.selectedTab = tab;
  };

  setSelectedEntityId = (id: string | null) => {
    this.selectedEntityId = id;
  };

  setEntityStatusFilter = (filter: EntityStatus | "all") => {
    this.entityStatusFilter = filter;
  };

  setEntityTypeFilter = (filter: EntityType | "all") => {
    this.entityTypeFilter = filter;
  };

  setAuditEventTypeFilter = (filter: AuditEventType | "all") => {
    this.auditEventTypeFilter = filter;
  };

  setAuditSeverityFilter = (filter: AuditSeverity | "all") => {
    this.auditSeverityFilter = filter;
  };

  setComplianceFrameworkFilter = (filter: ComplianceFramework | "all") => {
    this.complianceFrameworkFilter = filter;
  };

  setComplianceStatusFilter = (filter: ComplianceStatus | "all") => {
    this.complianceStatusFilter = filter;
  };

  setDateRangeFilter = (startDate: string, endDate: string) => {
    this.dateRangeFilter = { startDate, endDate };
  };

  // ===================================================================
  // ACTIONS - ENTITY MANAGEMENT
  // ===================================================================

  openEntityDialog = (entity?: LegalEntity) => {
    if (entity) {
      this.entityFormData = { ...entity };
      this.isEditMode = true;
    } else {
      this.entityFormData = this.getInitialEntityFormData();
      this.isEditMode = false;
    }
    this.entityDialogOpen = true;
  };

  closeEntityDialog = () => {
    this.entityDialogOpen = false;
    this.entityFormData = this.getInitialEntityFormData();
    this.isEditMode = false;
  };

  updateEntityFormData = (field: keyof LegalEntity, value: any) => {
    (this.entityFormData as any)[field] = value;
  };

  saveEntity = async () => {
    try {
      if (this.isEditMode && this.entityFormData.id) {
        await this.legalEntitiesStore.patch(
          this.entityFormData.id,
          this.entityFormData
        );
      } else {
        await this.legalEntitiesStore.create(this.entityFormData);
      }

      this.closeEntityDialog();
    } catch (error) {
      console.error("Failed to save entity:", error);
      throw error;
    }
  };

  deleteEntity = async (id: string) => {
    try {
      await this.legalEntitiesStore.remove(id);
    } catch (error) {
      console.error("Failed to delete entity:", error);
      throw error;
    }
  };

  // ===================================================================
  // ACTIONS - INTERCOMPANY TRANSACTIONS
  // ===================================================================

  openIntercompanyTxDialog = (transaction?: IntercompanyTransaction) => {
    if (transaction) {
      this.intercompanyTxFormData = { ...transaction };
      this.isEditMode = true;
    } else {
      this.intercompanyTxFormData = this.getInitialIntercompanyTxFormData();
      this.isEditMode = false;
    }
    this.intercompanyTxDialogOpen = true;
  };

  closeIntercompanyTxDialog = () => {
    this.intercompanyTxDialogOpen = false;
    this.intercompanyTxFormData = this.getInitialIntercompanyTxFormData();
    this.isEditMode = false;
  };

  saveIntercompanyTx = async () => {
    try {
      if (this.isEditMode && this.intercompanyTxFormData.id) {
        await this.intercompanyTransactionsStore.patch(
          this.intercompanyTxFormData.id,
          this.intercompanyTxFormData
        );
      } else {
        await this.intercompanyTransactionsStore.create(this.intercompanyTxFormData);
      }

      this.closeIntercompanyTxDialog();
    } catch (error) {
      console.error("Failed to save intercompany transaction:", error);
      throw error;
    }
  };

  // ===================================================================
  // ACTIONS - COMPLIANCE
  // ===================================================================

  openComplianceReportDialog = (report?: ComplianceReport) => {
    if (report) {
      this.complianceReportFormData = { ...report };
      this.isEditMode = true;
    } else {
      this.complianceReportFormData = this.getInitialComplianceReportFormData();
      this.isEditMode = false;
    }
    this.complianceReportDialogOpen = true;
  };

  closeComplianceReportDialog = () => {
    this.complianceReportDialogOpen = false;
    this.complianceReportFormData = this.getInitialComplianceReportFormData();
    this.isEditMode = false;
  };

  saveComplianceReport = async () => {
    try {
      if (this.isEditMode && this.complianceReportFormData.id) {
        await this.complianceReportsStore.patch(
          this.complianceReportFormData.id,
          this.complianceReportFormData
        );
      } else {
        await this.complianceReportsStore.create(this.complianceReportFormData);
      }

      this.closeComplianceReportDialog();
    } catch (error) {
      console.error("Failed to save compliance report:", error);
      throw error;
    }
  };

  openComplianceRuleDialog = (rule?: ComplianceRule) => {
    if (rule) {
      this.complianceRuleFormData = { ...rule };
      this.isEditMode = true;
    } else {
      this.complianceRuleFormData = this.getInitialComplianceRuleFormData();
      this.isEditMode = false;
    }
    this.complianceRuleDialogOpen = true;
  };

  closeComplianceRuleDialog = () => {
    this.complianceRuleDialogOpen = false;
    this.complianceRuleFormData = this.getInitialComplianceRuleFormData();
    this.isEditMode = false;
  };

  saveComplianceRule = async () => {
    try {
      if (this.isEditMode && this.complianceRuleFormData.id) {
        await this.complianceRulesStore.patch(
          this.complianceRuleFormData.id,
          this.complianceRuleFormData
        );
      } else {
        await this.complianceRulesStore.create(this.complianceRuleFormData);
      }

      this.closeComplianceRuleDialog();
    } catch (error) {
      console.error("Failed to save compliance rule:", error);
      throw error;
    }
  };

  // ===================================================================
  // INITIAL FORM DATA
  // ===================================================================

  getInitialEntityFormData(): Partial<LegalEntity> {
    return {
      entityCode: "",
      entityName: "",
      legalName: "",
      entityType: EntityType.SUBSIDIARY,
      taxId: "",
      incorporationDate: new Date().toISOString().split("T")[0],
      fiscalYearEnd: "12-31",
      baseCurrency: "USD",
      status: EntityStatus.ACTIVE,
      address: {
        street1: "",
        city: "",
        state: "",
        postalCode: "",
        country: "USA",
      },
      contact: {
        primaryContactName: "",
        primaryContactEmail: "",
        primaryContactPhone: "",
      },
      banking: [],
      settings: {
        defaultPaymentTerms: 30,
        defaultTaxRate: 0,
        allowIntercompanyTransactions: true,
        requireApprovalForIntercompany: false,
        autoEliminateIntercompany: false,
        consolidationMethod: "equity",
      },
      useParentCOA: false,
    };
  }

  getInitialIntercompanyTxFormData(): Partial<IntercompanyTransaction> {
    return {
      transactionType: IntercompanyTransactionType.SALE,
      status: IntercompanyTransactionStatus.PENDING,
      fromEntityId: "",
      toEntityId: "",
      transactionDate: new Date().toISOString().split("T")[0],
      amount: 0,
      currency: "USD",
      description: "",
    };
  }

  getInitialComplianceReportFormData(): Partial<ComplianceReport> {
    return {
      reportName: "",
      framework: ComplianceFramework.GAAP,
      reportingPeriod: new Date().toISOString().slice(0, 7),
      status: ComplianceStatus.PENDING_REVIEW,
      dueDate: new Date().toISOString().split("T")[0],
      sections: [],
      attachments: [],
    };
  }

  getInitialComplianceRuleFormData(): Partial<ComplianceRule> {
    return {
      ruleName: "",
      framework: ComplianceFramework.GAAP,
      ruleCode: "",
      description: "",
      requirement: "",
      category: "",
      severity: "medium",
      isActive: true,
      checkFrequency: "monthly",
    };
  }

  getInitialRetentionPolicyFormData(): Partial<DocumentRetentionPolicy> {
    return {
      policyName: "",
      documentType: "",
      retentionPeriod: 7,
      retentionTrigger: "creation_date",
      destructionMethod: "secure_delete",
      approvalRequired: true,
      isActive: true,
    };
  }

  getInitialCertificationFormData(): Partial<ComplianceCertification> {
    return {
      certificationType: "",
      certificationBody: "",
      certificateNumber: "",
      issueDate: new Date().toISOString().split("T")[0],
      expiryDate: "",
      status: "active",
      scope: "",
      renewalRequired: true,
      renewalNotificationDays: 90,
    };
  }

  // ===================================================================
  // DATA LOADING
  // ===================================================================

  loadData = async () => {
    try {
      await Promise.all([
        this.legalEntitiesStore?.find(),
        this.intercompanyTransactionsStore?.find(),
        this.consolidatedFinancialsStore?.find(),
        this.auditLogsStore?.find(),
        this.auditReportsStore?.find(),
        this.dataMigrationLogsStore?.find(),
        this.complianceReportsStore?.find(),
        this.complianceRulesStore?.find(),
        this.complianceViolationsStore?.find(),
        this.documentRetentionPoliciesStore?.find(),
        this.retainedDocumentsStore?.find(),
        this.complianceCertificationsStore?.find(),
      ]);
    } catch (error) {
      console.error("Failed to load advanced features data:", error);
    }
  };

  // ===================================================================
  // LIFECYCLE
  // ===================================================================

  cleanup = () => {
    this.selectedTab = 0;
    this.selectedEntityId = null;
    this.selectedAuditLogId = null;
    this.selectedComplianceReportId = null;
    this.entityFormData = this.getInitialEntityFormData();
    this.intercompanyTxFormData = this.getInitialIntercompanyTxFormData();
    this.complianceReportFormData = this.getInitialComplianceReportFormData();
    this.complianceRuleFormData = this.getInitialComplianceRuleFormData();
  };
}
